import {MobileService} from './modules/shared/services/mobile.service';
import {Component, OnDestroy, OnInit, Inject} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import * as dayjs from 'dayjs';
import 'dayjs/locale/it';
import * as customParseFormat from 'dayjs/plugin/customParseFormat';
import * as duration from 'dayjs/plugin/duration';
import {Observable, Subject} from 'rxjs';
import {CmsService} from './modules/cms/services/cms.service';
import {MetaService} from './modules/shared/services/meta.service';
import {PostMessageService} from './modules/shared/services/post-message.service';
import {WebsiteConfigService} from './modules/shared/services/website-config.service';
import {WidgetService} from './modules/shared/services/widget.service';
import {Networks, NetworkConfigs} from './modules/shared/models/networks.enum';
import {ProjectService} from './modules/shared/services/project.service';
import {PrerenderService} from "./services/prerender.service";
import {ScriptLoaderService} from "./services/script-loader.service";
import {NavigationEnd, Router} from '@angular/router';
import {takeUntil} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {maintenancePopup} from './enums/text-popups';

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit, OnDestroy {
  public isLoaded: boolean = false;
  public isLanding: boolean;
  // a differenza di isLanding (calcolato una sola volta al primo caricamento e mai più aggiornato) questo flag
  // riflette la route CORRENTE: serve per capire quando l'utente ha lasciato /landing (es. per proseguire nel
  // booking funnel) pur essendo ancora dentro una sessione iniziata da una landing page
  public currentUrlIsLanding: boolean = window.location.pathname.toLowerCase().includes('/landing');
  public isStoreLocatorWidget: boolean;
  public isQrPage: boolean;
  public menuOpen: boolean;

  private _destroy$ = new Subject<boolean>();

  isStoreLocatorWidget$: Observable<boolean>;
  isWidget$: Observable<boolean>;
  isB2B$: Observable<boolean>;
  isFlashViaggiConventionPage: boolean;
  isWTG: boolean;
  isPrerender: boolean;

  constructor(@Inject(DOCUMENT) private document: Document,
              private _websiteConfigService: WebsiteConfigService,
              private _widgetService: WidgetService,
              private _postMessageService: PostMessageService,
              private _metaService: MetaService,
              private _cmsService: CmsService,
              private _router: Router,
              private _projectService: ProjectService,
              private _mobileService: MobileService,
              private _prerenderService: PrerenderService,
              private _scriptLoaderService: ScriptLoaderService
  ) {
    this._router.events.subscribe((e) => {
      if (e instanceof NavigationEnd) {
        this.isFlashViaggiConventionPage = e.url.includes('convenzioni.html');
        this.currentUrlIsLanding = e.urlAfterRedirects.toLowerCase().includes('/landing');
      }
    });
    this._metaService.removeIndexingFromGoogle();
    this._mobileService.toggleSideBar$.pipe(takeUntil(this._destroy$)).subscribe(toggle => {
      this.menuOpen = toggle;
    })

    this.isPrerender = this._prerenderService.isPrerender;
    if (!this.isPrerender) {
      this._scriptLoaderService.loadScript(true);
    }
  }

  ngOnInit(): void {
    dayjs.locale('it');
    dayjs.extend(customParseFormat);
    dayjs.extend(duration);

    this.isLanding = window.location.pathname.toLowerCase().includes("/landing");
    this.isQrPage = window.location.pathname.toLowerCase().includes("/qr");
    this.isStoreLocatorWidget$ = this._postMessageService.isStoreLocatorWidget();
    this.isStoreLocatorWidget$.subscribe((isStoreLocatorWidget) => {
      this.isStoreLocatorWidget = isStoreLocatorWidget;
      if (this.isLanding && !isStoreLocatorWidget) {
        this.showMaintenancePopup(false);
      }
      if (!this.isLanding) {
        this._projectService.parentUrl = window.location.origin;

        this.isWidget$ = this._widgetService.isWidget();
        this.isWidget$.subscribe(isWidget => {
          this._projectService.isWidget = isWidget;
          if (isWidget) {
            document.addEventListener('click', (e) => this.handleOcmPolicyClick(e));
            this._postMessageService.getOrigin().toPromise().then(
              (resolved) => {
                this._projectService.parentUrl = resolved;
              },
              (rejected) => {
                console.error(rejected);
              },
            );
            document.body.classList.add('is-widget');
          }
          if (!this.isStoreLocatorWidget) {
            if(!isWidget)this._cmsService.initialise();
            this.showMaintenancePopup(isWidget);
            this._websiteConfigService.getAnalytics().pipe(takeUntil(this._destroy$)).subscribe(analytics => {
              if (analytics) {
                const src = `https://www.googletagmanager.com/gtag/js?id=${analytics}`;
                this._scriptLoaderService.loadScript(false, src, 'text/javascript', 'head', false, true, !!analytics);
              }
            });
            this._websiteConfigService.getFbPixel().pipe(takeUntil(this._destroy$)).subscribe(fbPixel => {
              if (fbPixel) {
                this._scriptLoaderService.loadScript(false, '', 'text/javascript', 'head', false, true, false, true, false, fbPixel);
              }
            });
            this._websiteConfigService.getTagManager().pipe(takeUntil(this._destroy$)).subscribe(tagManager => {
              if (tagManager) {
                this._scriptLoaderService.loadScript(false, '', 'text/javascript', 'head', false, true, false, false, true, tagManager);
              }
            });
            this._websiteConfigService.getCustomJsHtml().pipe(takeUntil(this._destroy$)).subscribe(e => {
              if (e) {
                const customScriptString = e.trim();
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = customScriptString;

                let headContent = '';
                let bodyContent = '';

                tempDiv.querySelectorAll('*').forEach(element => {
                  element.classList.add('custom');
                  if (element.nodeName === 'SCRIPT') {
                    headContent += element.outerHTML;
                  } else if (element.nodeName === 'NOSCRIPT') {
                    bodyContent += element.outerHTML;
                  }
                });
                if (headContent) {
                  if (!this.isPrerender) {
                    this.processElements(headContent, 'script', 'custom-head', document.head);
                  }
                }

                if (bodyContent) {
                  this.processElements(bodyContent, 'noscript', 'custom-body', document.body);
                }
              }
            });
            setTimeout(() => {
              ocm?.init();
            }, 1000);
          }
        });

        if (this.isStoreLocatorWidget) {
          this.forceWtgStyle();
        } else {
          this._projectService.getAgencyNetwork().toPromise().then(
            (resolve) => {
              console.log('resolve', resolve)
              this.isWTG = Number(resolve.id_network) && Number(resolve.id_network) === Networks.WTG;
              //this._projectService.network = Number(resolve.id_network) === Networks.WTG ? 'WTG' : 'GEO';
              const networkConfig = NetworkConfigs[Number(resolve.id_network)];
              this._projectService.network = networkConfig.network;
              this.loadPartnerStyle(networkConfig);

              if (this.isWTG) {
                document.body.classList.add('is-wtg');
                // imposto la favicon dedicata ai siti wtg
                document.querySelector('#custom-favicon').setAttribute('href', 'favicon_wtg.ico');
              } else {
                document.querySelector('#custom-favicon').setAttribute('href', 'favicon_geo.ico');
              }
            },
            (reject) => {
              console.error(reject);
            }
          );
          this._projectService.getProfile().toPromise().then(
            (resolved) => {
              if (resolved?.email_1?.trim()?.length === 0 && resolved?.email_2?.trim()?.length === 0) {
                alert('Configurazione errata.');
                window.stop();
                throw new Error('Configurazione Errata del sito.');
              }
            }
          );
        }

        // Temporary HACK
        this._cmsService.hasCmsEnabled$.pipe(takeUntil(this._destroy$)).subscribe(hasCms => {
          if (hasCms != undefined) {
            setTimeout(() => {
              this.isLoaded = true;
            }, 450)
          }
        })
      }
    });
  }


  private showMaintenancePopup(isWidget: boolean): void {
    if (!maintenancePopup.enabled || this.isPrerender) {
      return;
    }
    // inside the widget iframe the popup is centered on the iframe (often ~550px high), so the full version gets cut on mobile
    Swal.fire({
      title: maintenancePopup.title,
      html: maintenancePopup.html,
      icon: isWidget ? undefined : 'info',
      showConfirmButton: false,
      showCloseButton: true,
      iconColor: '#4ea4d7',
      customClass: isWidget ? {popup: 'maintenance-popup--compact'} : undefined,
    });
  }

  private handleOcmPolicyClick(e: Event): void {
    const target = e.target as HTMLElement;
    const isOcmPolicyLink = target.closest('.ocm-panel__link-policy') ||
      (target.tagName === 'A' &&
        target.getAttribute('href')?.includes('cookie-policy') &&
        (target.closest('.ocm-panel__description') || target.closest('.ocm-panel__description-inside')));

    if (isOcmPolicyLink) {
      e.preventDefault();
      this._router.navigate(['cookie-policy']);
      const panel = document.querySelector('.ocm-panel') as HTMLElement;
      if (panel) {
        panel.style.display = 'none';
      }
    }
  }

  private processElements(container, elementType, className, parent) {
    const element = document.createElement('div');
    element.innerHTML = container;
    element.querySelectorAll(elementType).forEach(originalElement => {
      const newElement = document.createElement(elementType);
      Array.from(originalElement.attributes).forEach((attr: Attr) => {
        if (attr.name != 'class') {
          newElement.setAttribute(attr.name, attr.value);
        }
      });
      newElement.innerHTML = originalElement.innerHTML;
      newElement.classList.add(className);
      parent.appendChild(newElement);
    });
  }

  ngOnDestroy(): void {
    this._destroy$.next(true);
    this._destroy$.complete();
  }

  loadPartnerStyle(networkConfig: any): void {
    this._cmsService.brandConfig$.next(networkConfig);
    let bodyEl = this.document.getElementById("body-el");
    bodyEl.classList.add(networkConfig.bodyClass);

    this.document.getElementById('partner-style').setAttribute('href', networkConfig.cssFile);
  }

  forceWtgStyle(): void {
    const networkConfig = NetworkConfigs[16];
    this._projectService.network = "WTG";
    this._cmsService.brandConfig$.next(networkConfig);
    let bodyEl = this.document.getElementById("body-el");
    bodyEl.classList.add(networkConfig.bodyClass);
  }

}
